import { createAuthClient } from 'better-auth/react';
import {
  emailOTPClient,
  genericOAuthClient,
} from 'better-auth/client/plugins';

// Origin of abse-auth-service, where Better Auth's /api/auth/* is served
// (e.g. http://localhost:8090 in dev). Set via NX_OCW_SERVICES_URL.
const ORIGIN = (import.meta.env['NX_OCW_SERVICES_URL'] as string) ?? '';

// Feature flag: when 'true', the SPA authenticates via Better Auth instead of
// Cognito. Off by default → existing Cognito path is completely unchanged.
export const USE_BETTER_AUTH = import.meta.env['NX_USE_BETTER_AUTH'] === 'true';

// When 'true', the login screen shows ONLY the enterprise SSO buttons — the
// email/passwordless entry is hidden too (for SSO-only tenants).
export const SOCIAL_ONLY = import.meta.env['NX_SOCIAL_ONLY'] === 'true';

export const authClient = createAuthClient({
  baseURL: ORIGIN,
  plugins: [emailOTPClient(), genericOAuthClient()],
});

// Enterprise SSO provider ids — match the Cognito identity providers and the
// genericOAuth config in abse-auth-service. Used by the OCP / UM6P buttons.
export type SsoProvider = 'azureocp' | 'azureum6p';

// Start the broker login: Better Auth → Cognito Hosted UI → Azure AD (SAML) →
// back. The browser lands on `path` (absolute SPA URL, since the auth service
// is a different origin) after a successful sign-in.
export function signInWithSso(providerId: SsoProvider, path = '/') {
  const callbackURL = `${window.location.origin}${path}`;
  return authClient.signIn.oauth2({ providerId, callbackURL, errorCallbackURL: `${window.location.origin}/auth/login` });
}

// --- JWT for Apollo / Hasura ------------------------------------------------
// Better Auth issues a short-lived (~15m) JWT at /api/auth/token. We cache it and
// refresh shortly before expiry. The gcal session-shim hands this to Apollo, so
// apollo/index.tsx (getAccessToken().getJwtToken()) needs no changes.
let cache: { jwt: string; exp: number } = { jwt: '', exp: 0 };

function expOf(jwt: string): number {
  try {
    return JSON.parse(atob(jwt.split('.')[1]))?.exp ?? 0;
  } catch {
    return 0;
  }
}

export async function getBetterAuthJwt(): Promise<string> {
  const now = Date.now() / 1000;
  if (cache.jwt && cache.exp - now > 60) return cache.jwt;
  const res = await fetch(`${ORIGIN}/api/auth/token`, { credentials: 'include' });
  if (!res.ok) throw new Error('better-auth: could not fetch token');
  const { token } = await res.json();
  cache = { jwt: token, exp: expOf(token) };
  return token;
}

export function clearBetterAuthJwt() {
  cache = { jwt: '', exp: 0 };
}
